import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { MapPin, ShoppingCart, Search, Truck, Wrench, ChevronRight } from 'lucide-react';
import SEOHead from '@/components/marketplace/SEOHead';
import { resolveEffectiveOgImage, absoluteUrl } from '@/lib/ogImage';
import ListingCard from '@/components/marketplace/ListingCard';
import FramedImage from '@/components/marketplace/FramedImage';

const TYPE_META = [
  { key: 'sell', path: '/sell', label: 'For Sale', icon: ShoppingCart },
  { key: 'wanted', path: '/wanted', label: 'Wanted', icon: Search },
  { key: 'rent', path: '/rent', label: 'For Rent', icon: Truck },
  { key: 'service', path: '/services', label: 'Services', icon: Wrench },
];

const LocationPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/location-pages/by-slug/${slug}`)
      .then((r) => {
        setPage(r.data);
        return api.get('/listings/public', { params: { city: r.data.city, limit: 8, sort: 'latest', strict_city: true } });
      })
      .then((r) => setListings(r?.data?.items || []))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className='indus-container py-16 text-center text-muted-foreground'>Loading…</div>;
  if (notFound || !page) {
    return (
      <div className='indus-container py-16 text-center' data-testid='location-page-notfound'>
        <SEOHead title='Location not found | IndusFact' robots='noindex,follow' />
        Location page not found. <Link to='/locations' className='text-primary hover:underline'>Browse all locations</Link>
      </div>
    );
  }

  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/locations/${page.slug}` : undefined;
  const cityQuery = `city=${encodeURIComponent(page.city)}&state=${encodeURIComponent(page.state)}`;

  return (
    <div className='indus-container py-6' data-testid='location-page'>
      <SEOHead
        title={page.meta_title || `${page.title} | IndusFact`}
        description={page.meta_description}
        keywords={page.focus_keyword}
        canonical={page.canonical_url || canonical}
        robots={page.robots_meta || 'index,follow'}
        image={absoluteUrl(resolveEffectiveOgImage(page, 'page'))}
        schemaEndpoint={`/seo/location/${page.slug}`}
      />

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to='/'>Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to='/locations'>Locations</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage>{page.city}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Hero — fixed 1336×420 (3.18:1) canvas. The banner is fitted with
          `contain` so its aspect ratio is preserved (never stretched/cropped);
          any leftover space is filled by a blurred copy of the same image. */}
      <div className='mt-4 rounded-xl border bg-secondary/30 overflow-hidden'>
        {page.hero_image_url && (
          <figure className='m-0'>
            <FramedImage
              src={page.hero_image_url}
              alt={page.hero_image_alt || page.title}
              ratio='aspect-[1336/420]'
              testId='location-page-hero-image'
            />
            {page.hero_image_caption && (
              <figcaption className='px-6 sm:px-8 pt-2 text-xs text-muted-foreground' data-testid='location-page-hero-caption'>
                {page.hero_image_caption}
              </figcaption>
            )}
          </figure>
        )}
        <div className='p-6 sm:p-8'>
          <div className='inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 rounded-full px-2.5 py-1'>
            <MapPin className='h-3.5 w-3.5' /> {page.city}, {page.state}
          </div>
          <h1 className='mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight' data-testid='location-page-title'>
            {page.title}
          </h1>
          <p className='mt-2 text-sm text-muted-foreground'>
            {page.counts?.total || 0} active listing{(page.counts?.total || 0) === 1 ? '' : 's'} from verified suppliers in {page.city}.
          </p>
        </div>
      </div>

      {/* Type quick-links */}
      <div className='mt-5 grid grid-cols-2 md:grid-cols-4 gap-3' data-testid='location-type-links'>
        {TYPE_META.map(({ key, path, label, icon: Icon }) => (
          <Link key={key} to={`${path}?${cityQuery}`} data-testid={`location-type-${key}`}>
            <Card className='p-4 h-full hover:shadow-md hover:border-primary/40 transition-all flex items-center justify-between gap-2'>
              <div className='flex items-center gap-2.5'>
                <span className='rounded-lg bg-secondary p-2'><Icon className='h-4 w-4 text-primary' /></span>
                <div>
                  <div className='font-semibold text-sm'>{label}</div>
                  <div className='text-xs text-muted-foreground'>{page.counts?.[key] || 0} in {page.city}</div>
                </div>
              </div>
              <ChevronRight className='h-4 w-4 text-muted-foreground' />
            </Card>
          </Link>
        ))}
      </div>

      {/* Latest listings */}
      {listings.length > 0 && (
        <section className='mt-8'>
          <div className='flex items-end justify-between mb-4'>
            <h2 className='text-lg md:text-lg font-semibold tracking-tight'>Latest in {page.city}</h2>
            <Link to={`/sell?${cityQuery}`} className='text-sm text-primary hover:underline inline-flex items-center gap-1'>
              View all <ChevronRight className='h-4 w-4' />
            </Link>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' data-testid='location-listings-grid'>
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      {/* Editorial content */}
      {page.html_content && (
        <Card className='mt-8 p-6 sm:p-8'>
          <div
            className='indus-prose text-[15px] leading-7'
            dangerouslySetInnerHTML={{ __html: page.html_content }}
          />
        </Card>
      )}

      {/* CTA */}
      <Card className='mt-6 p-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-primary/5 border-primary/20'>
        <div>
          <div className='font-semibold'>Have something to sell or need to buy in {page.city}?</div>
          <div className='text-sm text-muted-foreground'>Post free and reach verified buyers &amp; sellers across {page.state}.</div>
        </div>
        <Button asChild data-testid='location-cta-post'>
          <Link to='/post'>Post a Listing</Link>
        </Button>
      </Card>
    </div>
  );
};

export default LocationPage;
